import { useEffect, useState } from 'react';
import { BottomSheet } from './BottomSheet';
import { EntityMultiSelectBody, type SelectedEntity } from '../EntityFilterSelect';
import { SavedFilterPicker } from '../SavedFilterPicker';
import { useProjectSearchSource, useWorkerSearchSource } from '../../hooks/useEntitySearchSource';
import { useSavedFilters } from '../../hooks/useSavedFilters';
import { EMPTY_FILTERS, filterCount, sameFilters } from '../../lib/scheduleFilters';
import type { FilterValue } from '../schedule/ScheduleFilters';
import type { SavedFilterDto } from '../../lib/types';

/** A `<select>` option — `value` is what the API filters on, `label` is what the user reads. */
export interface FilterOption {
  value: string;
  label: string;
}

export interface ScheduleFiltersSheetProps {
  open: boolean;
  /** Discards staged changes. Also what Esc / backdrop / × do. */
  onClose: () => void;
  /** The currently *applied* filter — the sheet stages a copy of this. */
  value: FilterValue;
  /** Called exactly once, on Apply. Triggers the single refetch. */
  onApply: (next: FilterValue) => void;
  appliedSavedFilter: SavedFilterDto | null;
  onApplySavedFilter: (filter: SavedFilterDto | null) => void;
  /** Region codes + display names. The API filters on the code. */
  regions: FilterOption[];
  offices: string[];
  serviceLines: string[];
}

const SECTION_LABEL = 'text-sm font-medium text-brand-charcoal mb-1 block';
// 16px text isn't a style choice: below it, iOS zooms the whole page when the control focuses.
const SELECT_CLASS =
  'w-full min-h-[44px] text-base border border-slate-300 rounded-lg px-2 bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-blue/30';

/**
 * The mobile filter surface. Everything the desktop filter bar spreads across a toolbar,
 * stacked in a bottom sheet.
 *
 * The sheet stages a **local copy** of the filter value: tapping around inside it costs
 * nothing, and exactly one `/api/schedule` request fires when Apply is pressed. Closing by
 * any other route (×, Esc, backdrop, drag) discards the staging with no confirmation — the
 * app is read-only, nothing is lost but a few taps, and a "discard changes?" prompt on a
 * filter panel just trains people to tap through dialogs.
 *
 * Mount it unconditionally; it renders nothing while closed.
 */
export function ScheduleFiltersSheet(props: ScheduleFiltersSheetProps) {
  // Remounting on every open is what resets the staged copy — no effect syncing props into
  // state, no stale staging left over from the last time the sheet was open.
  if (!props.open) return null;
  return <FiltersSheetBody {...props} />;
}

function FiltersSheetBody({
  onClose,
  value,
  onApply,
  appliedSavedFilter,
  onApplySavedFilter,
  regions,
  offices,
  serviceLines,
}: ScheduleFiltersSheetProps) {
  const [staged, setStaged] = useState<FilterValue>(value);
  const [stagedSaved, setStagedSaved] = useState<SavedFilterDto | null>(appliedSavedFilter);
  // Collapsed by default, but a facet that already has a selection opens with the sheet —
  // otherwise the reason the list looks short is hidden one tap deep.
  const [workersOpen, setWorkersOpen] = useState(value.people.length > 0);
  const [projectsOpen, setProjectsOpen] = useState(value.projects.length > 0);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: savedFilters, isLoading: savedLoading } = useSavedFilters();

  // `SavedFilterPicker` is a `<Modal>` opening on top of a `<BottomSheet>`, and both listen
  // for Escape on `document`. Without this, one Esc meant for the picker would also tear
  // down the sheet behind it and throw away everything staged. Intercepting at the capture
  // phase stops the event before either bubble-phase listener sees it, so the top dialog is
  // the only one that closes.
  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setPickerOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [pickerOpen]);

  const stagedCount = filterCount(staged);
  const unchanged = sameFilters(staged, value) && stagedSaved?.id === appliedSavedFilter?.id;
  const canSave = staged.people.length > 0 || staged.projects.length > 0;

  function handleApply() {
    // A saved filter owns the people/projects selection, so it has to land first —
    // `changeFilters` then re-checks whether the selection still matches what was saved.
    if (stagedSaved?.id !== appliedSavedFilter?.id) onApplySavedFilter(stagedSaved);
    onApply(staged);
    onClose();
  }

  function toggleSavedFilter(f: SavedFilterDto) {
    if (stagedSaved?.id === f.id) {
      setStagedSaved(null);
      setStaged((s) => ({ ...s, people: [], projects: [] }));
      return;
    }
    setStagedSaved(f);
    setStaged((s) => ({
      ...s,
      people: f.personIds.map((id, i) => ({ id, label: f.personNames[i] ?? String(id) })),
      projects: f.projectIds.map((id, i) => ({ id, label: f.projectNames[i] ?? String(id) })),
    }));
  }

  function handleReset() {
    setStaged(EMPTY_FILTERS);
    setStagedSaved(null);
    setWorkersOpen(false);
    setProjectsOpen(false);
  }

  return (
    <>
      <BottomSheet
        open
        onClose={onClose}
        title="Filters"
        headerAction={
          stagedCount > 0 || stagedSaved ? (
            <button
              type="button"
              onClick={handleReset}
              className="min-h-[44px] px-3 text-sm font-medium text-brand-blue"
            >
              Reset
            </button>
          ) : undefined
        }
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={!canSave}
              className="min-h-[48px] px-4 rounded-lg border border-slate-300 text-sm font-medium text-brand-charcoal disabled:opacity-50"
            >
              Save as filter…
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 min-h-[48px] rounded-lg bg-brand-blue text-white font-semibold"
            >
              {unchanged ? 'Done' : 'Apply'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Saved filters
            </h3>
            {savedLoading && <p className="text-xs text-slate-500">Loading…</p>}
            {!savedLoading && (savedFilters ?? []).length === 0 && (
              <p className="text-xs text-slate-500">No saved filters yet.</p>
            )}
            {(savedFilters ?? []).length > 0 && (
              <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
                {(savedFilters ?? []).map((f) => {
                  const selected = stagedSaved?.id === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleSavedFilter(f)}
                      aria-pressed={selected}
                      className={`shrink-0 min-h-[44px] px-4 rounded-full text-sm font-medium max-w-[200px] truncate ${
                        selected ? 'bg-brand-blue text-white' : 'bg-slate-100 text-brand-charcoal'
                      }`}
                    >
                      {f.name}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <div>
            <label htmlFor="mf-region" className={SECTION_LABEL}>
              Region
            </label>
            <select
              id="mf-region"
              value={staged.region}
              onChange={(e) => setStaged((s) => ({ ...s, region: e.target.value }))}
              className={SELECT_CLASS}
            >
              <option value="">All regions</option>
              {regions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="mf-office" className={SECTION_LABEL}>
              Office
            </label>
            <select
              id="mf-office"
              value={staged.office}
              onChange={(e) => setStaged((s) => ({ ...s, office: e.target.value }))}
              className={SELECT_CLASS}
            >
              <option value="">All offices</option>
              {offices.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="mf-service-line" className={SECTION_LABEL}>
              Service line
            </label>
            <select
              id="mf-service-line"
              value={staged.serviceLine}
              onChange={(e) => setStaged((s) => ({ ...s, serviceLine: e.target.value }))}
              className={SELECT_CLASS}
            >
              <option value="">All service lines</option>
              {serviceLines.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <ExpandableFacet
            label="Workers"
            emptySummary="All workers"
            selected={staged.people}
            open={workersOpen}
            onToggle={() => setWorkersOpen((o) => !o)}
          >
            <WorkersFacetBody
              selected={staged.people}
              onChange={(people) => setStaged((s) => ({ ...s, people }))}
            />
          </ExpandableFacet>

          <ExpandableFacet
            label="Projects"
            emptySummary="All projects"
            selected={staged.projects}
            open={projectsOpen}
            onToggle={() => setProjectsOpen((o) => !o)}
          >
            <ProjectsFacetBody
              selected={staged.projects}
              onChange={(projects) => setStaged((s) => ({ ...s, projects }))}
            />
          </ExpandableFacet>
        </div>
      </BottomSheet>

      {/* `<Modal>` sits at z-40, the sheet at z-50 — without a stacking context of its own
          the picker would open *behind* the sheet that launched it. */}
      <div className="relative z-[60]">
        <SavedFilterPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          editingFilter={null}
          initialPersonIds={staged.people.map((p) => p.id)}
          initialPersonNames={staged.people.map((p) => p.label)}
          initialProjectIds={staged.projects.map((p) => p.id)}
          initialProjectNames={staged.projects.map((p) => p.label)}
          onSaved={(saved) => setStagedSaved(saved)}
        />
      </div>
    </>
  );
}

interface ExpandableFacetProps {
  label: string;
  /** Right-hand summary when nothing is selected, e.g. `All workers`. */
  emptySummary: string;
  selected: SelectedEntity[];
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function ExpandableFacet({
  label,
  emptySummary,
  selected,
  open,
  onToggle,
  children,
}: ExpandableFacetProps) {
  const summary =
    selected.length === 0
      ? emptySummary
      : selected.length === 1
        ? selected[0].label
        : `${selected.length} selected`;

  return (
    <div className="border-t border-slate-200 pt-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full min-h-[44px] flex items-center justify-between gap-3 text-left"
      >
        <span className="flex items-center gap-2 shrink-0">
          <span aria-hidden="true" className="text-slate-400 text-xs w-3">
            {open ? '▾' : '▸'}
          </span>
          <span className="text-sm font-medium text-brand-charcoal">{label}</span>
        </span>
        <span className="text-sm text-slate-500 truncate">{summary}</span>
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  );
}

// Each facet body owns its own search hook, so opening the sheet doesn't fire a people and a
// projects search for sections the user never expands.
function WorkersFacetBody({
  selected,
  onChange,
}: {
  selected: SelectedEntity[];
  onChange: (next: SelectedEntity[]) => void;
}) {
  const source = useWorkerSearchSource();
  return <EntityMultiSelectBody {...source} label="Workers" selected={selected} onChange={onChange} />;
}

function ProjectsFacetBody({
  selected,
  onChange,
}: {
  selected: SelectedEntity[];
  onChange: (next: SelectedEntity[]) => void;
}) {
  const source = useProjectSearchSource();
  return <EntityMultiSelectBody {...source} label="Projects" selected={selected} onChange={onChange} />;
}
